import apiClient from "../lib/api-client";

export type PurchaseInvoiceStatus = "DRAFT" | "POSTED" | "VOIDED";

export interface PurchaseInvoiceLineInput {
  sourceLineId: string;
  productId: string;
  goodsReceiptItemId?: string;
  quantityMilli: string;
  unitCostPaise: string;
  discountPaise?: string;
  taxablePaise: string;
  rateBps: number;
  cgstPaise?: string;
  sgstPaise?: string;
  igstPaise?: string;
  cessPaise?: string;
}

export interface CreatePurchaseInvoiceInput {
  supplierId: string;
  goodsReceiptId?: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  lines: PurchaseInvoiceLineInput[];
  discountPaise?: string;
  freightPaise?: string;
  handlingPaise?: string;
  otherChargesPaise?: string;
  tcsPaise?: string;
  roundingPaise?: string;
}

export interface PurchaseInvoice {
  id: string;
  tenantId: string;
  storeId: string;
  supplierId: string;
  goodsReceiptId?: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  subtotalPaise: string;
  discountPaise: string;
  freightPaise: string;
  handlingPaise: string;
  otherChargesPaise: string;
  taxPaise: string;
  tcsPaise: string;
  roundingPaise: string;
  totalPaise: string;
  status: PurchaseInvoiceStatus;
  postedAt?: string;
  createdAt?: string;
  supplier?: { id: string; name: string; phone?: string; email?: string };
  goodsReceipt?: {
    id: string;
    grnNumber: string;
    grnDate: string;
    status: string;
  };
  lines: Array<
    PurchaseInvoiceLineInput & {
      id: string;
      taxPaise: string;
      totalPaise: string;
      product?: { id: string; name: string; sku?: string };
    }
  >;
}

export const PURCHASE_INVOICE_STATUS_LABELS: Record<
  PurchaseInvoiceStatus,
  string
> = {
  DRAFT: "Draft",
  POSTED: "Posted",
  VOIDED: "Voided",
};

export const PURCHASE_INVOICE_STATUS_COLORS: Record<
  PurchaseInvoiceStatus,
  string
> = {
  DRAFT: "bg-yellow-100 text-yellow-800",
  POSTED: "bg-green-100 text-green-800",
  VOIDED: "bg-red-100 text-red-800",
};

function mutationKey(key: string) {
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const next = crypto.randomUUID();
  sessionStorage.setItem(key, next);
  return next;
}

export const purchaseInvoiceService = {
  async list(params?: {
    status?: PurchaseInvoiceStatus;
    supplierId?: string;
  }): Promise<PurchaseInvoice[]> {
    const response = await apiClient.get("/purchase-invoices", { params });
    return response.data;
  },

  async get(id: string): Promise<PurchaseInvoice> {
    const response = await apiClient.get(`/purchase-invoices/${id}`);
    return response.data;
  },

  async create(data: CreatePurchaseInvoiceInput): Promise<PurchaseInvoice> {
    const storageKey = `medicorepay:purchase-invoice:create:${data.supplierId}:${data.invoiceNumber}`;
    const response = await apiClient.post("/purchase-invoices", data, {
      headers: { "Idempotency-Key": mutationKey(storageKey) },
    });
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async post(id: string): Promise<PurchaseInvoice> {
    const storageKey = `medicorepay:purchase-invoice:post:${id}`;
    const response = await apiClient.post(
      `/purchase-invoices/${id}/post`,
      undefined,
      {
        headers: { "Idempotency-Key": mutationKey(storageKey) },
      },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },

  async void(id: string, reason: string): Promise<PurchaseInvoice> {
    const storageKey = `medicorepay:purchase-invoice:void:${id}`;
    const response = await apiClient.post(
      `/purchase-invoices/${id}/void`,
      { reason },
      {
        headers: { "Idempotency-Key": mutationKey(storageKey) },
      },
    );
    sessionStorage.removeItem(storageKey);
    return response.data;
  },
};
